import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeftIcon, CpuChipIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

type Period = 'day' | 'week' | 'month';

interface SystemMetrics {
  period: Period;
  startDate: string;
  tokenUsage: Array<{
    model: string;
    totalTokens: number;
    promptTokens: number;
    completionTokens: number;
    chargedAmount: number;
    callCount: number;
  }>;
  aiCalls: Array<{ type: string; count: number }>;
  writing: { storiesCreated: number; chaptersUpdated: number; activeWriters: number };
}

const PERIODS: Array<{ id: Period; label: string }> = [
  { id: 'day', label: 'Today' },
  { id: 'week', label: 'Last 7 days' },
  { id: 'month', label: 'This month' },
];

const AdminMetrics: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('day');
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/admin/metrics?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMetrics(response.data);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Admin access required');
        navigate('/login');
      } else {
        setError('Failed to load metrics');
      }
    } finally {
      setLoading(false);
    }
  }, [period, navigate]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const totalTokens = metrics?.tokenUsage.reduce((s, u) => s + u.totalTokens, 0) ?? 0;
  const totalCharged = metrics?.tokenUsage.reduce((s, u) => s + u.chargedAmount, 0) ?? 0;
  const totalCalls = metrics?.tokenUsage.reduce((s, u) => s + u.callCount, 0) ?? 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Metrics</h1>
          <div className="flex gap-1 bg-white rounded-lg shadow p-1">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  period === p.id ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-red-600 mb-4">{error}</p>}
        {loading && !metrics ? (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">AI usage</h2>
                <CpuChipIcon className="w-6 h-6 text-gray-400" />
              </div>
              <dl className="grid grid-cols-3 gap-4 text-sm mb-4">
                <div>
                  <dt className="text-gray-500">Calls</dt>
                  <dd className="text-xl font-semibold">{totalCalls}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Tokens</dt>
                  <dd className="text-xl font-semibold">{totalTokens.toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Charged</dt>
                  <dd className="text-xl font-semibold">${totalCharged.toFixed(2)}</dd>
                </div>
              </dl>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase">
                    <th className="py-2 pr-4">Model</th>
                    <th className="py-2 pr-4 text-right">Calls</th>
                    <th className="py-2 pr-4 text-right">Tokens</th>
                    <th className="py-2 text-right">Charged</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(metrics?.tokenUsage ?? []).map((u) => (
                    <tr key={u.model}>
                      <td className="py-2 pr-4">{u.model}</td>
                      <td className="py-2 pr-4 text-right">{u.callCount}</td>
                      <td className="py-2 pr-4 text-right">{u.totalTokens.toLocaleString()}</td>
                      <td className="py-2 text-right">${u.chargedAmount.toFixed(2)}</td>
                    </tr>
                  ))}
                  {metrics?.tokenUsage.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-3 text-gray-500">
                        No AI usage in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {metrics && metrics.aiCalls.length > 0 && (
                <p className="text-xs text-gray-500 mt-3">
                  By type: {metrics.aiCalls.map((a) => `${a.type} ${a.count}`).join(' · ')}
                </p>
              )}
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Writing</h2>
                <PencilSquareIcon className="w-6 h-6 text-gray-400" />
              </div>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-600">Stories created</dt>
                  <dd className="font-medium">{metrics?.writing.storiesCreated ?? 0}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">Chapters updated</dt>
                  <dd className="font-medium">{metrics?.writing.chaptersUpdated ?? 0}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">Writers using AI</dt>
                  <dd className="font-medium">{metrics?.writing.activeWriters ?? 0}</dd>
                </div>
              </dl>
              {metrics && (
                <p className="text-xs text-gray-400 mt-4">
                  Since {new Date(metrics.startDate).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            type="button"
            onClick={fetchMetrics}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMetrics;
